import classNames from 'classnames';
import { forwardRef, useContext } from 'react';
import { Button, type ButtonProps } from '../Button';
import { TooltipBubble } from '../TooltipBubble';
import { NavbarContext } from './NavbarContext';
import type { NavbarTheme } from './types';

type NavbarButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link';

const buttonVariants: Record<
  NavbarTheme,
  Record<
    NavbarButtonVariant,
    Pick<
      ButtonProps,
      | 'secondary'
      | 'link'
      | 'dark'
      | 'darkSecondary'
      | 'minimalDark'
      | 'linkLight'
      | 'linkDark'
      | 'textColor'
    >
  >
> = {
  light: {
    primary: {},
    secondary: {
      secondary: true,
    },
    tertiary: {
      secondary: true,
      link: true,
    },
    link: {
      linkLight: true,
    },
  },
  dark: {
    primary: {
      dark: true,
    },
    secondary: {
      darkSecondary: true,
    },
    tertiary: {
      minimalDark: true,
    },
    link: {
      linkDark: true,
    },
  },
};

export interface NavbarButtonProps
  extends Pick<
    ButtonProps,
    'disabled' | 'icon' | 'leftIcon' | 'rightIcon' | 'onClick' | 'label'
  > {
  variant?: NavbarButtonVariant;
  tooltipMessage?: string;
}

export const NavbarButton = forwardRef<HTMLButtonElement, NavbarButtonProps>(
  (
    {
      variant = 'primary',
      disabled,
      icon,
      leftIcon,
      rightIcon,
      onClick,
      label,
      tooltipMessage,
    },
    ref
  ) => {
    const { theme: navbarTheme = 'light' } = useContext(NavbarContext);
    const buttonVariantProps = buttonVariants[navbarTheme][variant];

    return (
      <TooltipBubble
        placement="top"
        title={tooltipMessage}
        disabled={!tooltipMessage}
      >
        <div
          className={classNames(
            navbarTheme === 'dark'
              ? 'text-ds-accent3-100'
              : 'text-ds-natural-600'
          )}
        >
          <Button
            ref={ref}
            icon={icon}
            rightIcon={rightIcon}
            leftIcon={leftIcon}
            label={label}
            disabled={disabled}
            onClick={onClick}
            {...buttonVariantProps}
          />
        </div>
      </TooltipBubble>
    );
  }
);
